import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * Ardi outside the chat box: what it is doing on its own (status, activity),
 * the chat apps it can be reached on (channels), and the standing instructions
 * it runs on a schedule (routines).
 */

export type ChannelKind = 'telegram' | 'slack' | 'whatsapp' | 'webhook';

export interface ArdiActivityItem {
  _id: string;
  kind: 'noticed' | 'lead' | 'routine' | 'channel' | 'watch' | 'pulse';
  key: string;
  data: Record<string, any>;
  route?: string | null;
  createdAt: string;
}

export interface ArdiStatus {
  on: boolean;
  lastPulseAt: string | null;
  checksToday: number;
  eventsToday: number;
  noticedToday: number;
  lastHour: { visitors: number; events: number };
  lastEventAt: string | null;
  silent: boolean;
  /** Everything Ardi did on its own in the last 24 hours. */
  activity24h: number;
  /** Of that, what it noticed (not what it did when asked). */
  noticed24h: number;
  recent: ArdiActivityItem[];
  channels: { connected: number; chats: number; kinds: ChannelKind[] };
  routines: { active: number; next: { title: string; at: string } | null };
}

export interface ChannelLinkView {
  externalId: string;
  label: string;
  linkedAt: string;
  lastMessageAt?: string | null;
  threadUuid?: string | null;
}

export interface ArdiChannel {
  uuid: string;
  kind: ChannelKind;
  name: string;
  status: 'pending' | 'connected' | 'error';
  info: Record<string, any>;
  conversational: boolean;
  /** Linked chats may approve or decline Ardi's proposals. */
  allowApprovals: boolean;
  notifyTypes: string[];
  links: ChannelLinkView[];
  pairing: { code: string; expiresAt: string; deepLink?: string } | null;
  stats: { messagesIn: number; messagesOut: number; lastActivityAt?: string | null };
  lastError?: string | null;
  lastErrorAt?: string | null;
  needsReconnect: boolean;
  createdAt: string;
  setup: { inboundUrl: string | null; manifest?: Record<string, any>; verifyToken?: string | null; token?: string | null };
}

export interface ChannelsResponse {
  channels: ArdiChannel[];
  notificationTypes: { type: string; category: string }[];
  defaults: string[];
}

export interface ChannelMessage {
  _id: string;
  direction: 'in' | 'out';
  externalId: string;
  preview: string;
  status: 'ok' | 'failed' | 'ignored' | 'limited';
  reason?: string | null;
  error?: string | null;
  createdAt: string;
}

export interface RoutineSchedule {
  every: 'day' | 'weekday' | 'week';
  hour: number;
  minute: number;
  weekday?: number;
  timezone: string;
}

export interface ArdiRoutine {
  uuid: string;
  title: string;
  prompt: string;
  template: string | null;
  schedule: RoutineSchedule;
  deliverTo: string[];
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: 'ok' | 'failed' | 'skipped' | null;
  lastSummary: string | null;
  lastError: string | null;
  threadUuid: string | null;
  runs: number;
  createdAt: string;
}

export interface RoutineTemplate {
  id: string;
  title: string;
  prompt: string;
  schedule: Omit<RoutineSchedule, 'timezone'>;
}

export interface RoutinesResponse {
  routines: ArdiRoutine[];
  templates: RoutineTemplate[];
  limit: number;
}

export type RoutineInput = Partial<Pick<ArdiRoutine, 'title' | 'prompt' | 'template' | 'deliverTo' | 'enabled'>> & { schedule?: Partial<RoutineSchedule> };

export const useArdiApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();
  const call = <T,>(method: ApiMethod, path: string, body?: any) => sendAuthGuardedRequest(method, path, body) as Promise<T>;

  return {
    getStatus: () => call<ArdiStatus>(ApiMethod.GET, '/services/pms/ardi/status'),
    getActivity: (params: { limit?: number; before?: string } = {}) =>
      call<ArdiActivityItem[]>(ApiMethod.GET, `/services/pms/ardi/activity?limit=${params.limit ?? 30}${params.before ? `&before=${encodeURIComponent(params.before)}` : ''}`),

    listChannels: () => call<ChannelsResponse>(ApiMethod.GET, '/services/pms/channels'),
    createChannel: (kind: ChannelKind, name?: string) => call<ArdiChannel>(ApiMethod.POST, '/services/pms/channels', { kind, ...(name ? { name } : {}) }),
    connectChannel: (uuid: string, credentials: Record<string, string>) => call<ArdiChannel>(ApiMethod.POST, `/services/pms/channels/${uuid}/connect`, credentials),
    newPairingCode: (uuid: string) => call<ArdiChannel>(ApiMethod.POST, `/services/pms/channels/${uuid}/pairing`),
    updateChannel: (uuid: string, patch: Partial<{ name: string; notifyTypes: string[]; conversational: boolean; outboundUrl: string | null; allowApprovals: boolean; interactive: boolean }>) =>
      call<ArdiChannel>(ApiMethod.PATCH, `/services/pms/channels/${uuid}`, patch),
    testChannel: (uuid: string) => call<{ sent: number; failed: number }>(ApiMethod.POST, `/services/pms/channels/${uuid}/test`),
    unlinkChat: (uuid: string, externalId: string) => call<ArdiChannel>(ApiMethod.DELETE, `/services/pms/channels/${uuid}/links/${encodeURIComponent(externalId)}`),
    removeChannel: (uuid: string) => call<{ removed: boolean }>(ApiMethod.DELETE, `/services/pms/channels/${uuid}`),
    channelMessages: (uuid: string) => call<ChannelMessage[]>(ApiMethod.GET, `/services/pms/channels/${uuid}/messages?limit=30`),

    listRoutines: () => call<RoutinesResponse>(ApiMethod.GET, '/services/pms/ardi/routines'),
    createRoutine: (input: RoutineInput) => call<ArdiRoutine>(ApiMethod.POST, '/services/pms/ardi/routines', input),
    updateRoutine: (uuid: string, input: RoutineInput) => call<ArdiRoutine>(ApiMethod.PATCH, `/services/pms/ardi/routines/${uuid}`, input),
    removeRoutine: (uuid: string) => call<{ removed: boolean }>(ApiMethod.DELETE, `/services/pms/ardi/routines/${uuid}`),
    runRoutine: (uuid: string) => call<ArdiRoutine>(ApiMethod.POST, `/services/pms/ardi/routines/${uuid}/run`),
  };
};
